/**
 * Tab-scoped session token mirror (sessionStorage).
 *
 * Survives in-tab refresh; cleared when the tab closes or the user logs out.
 * Tokens are never written to localStorage or redux-persist (HIPAA: no durable token/PHI at rest).
 * Keys are realm-only — no user identifiers.
 */

import { setAdminSessionTokens } from 'store/slices/adminSessionSlice';
import { setUserSessionTokens } from 'store/slices/userSessionSlice';

import type { SessionRealm } from './sessionRealm';
import type { Dispatch } from '@reduxjs/toolkit';

export const USER_TAB_SESSION_STORAGE_KEY = 'spence_tab_session_user';
export const ADMIN_TAB_SESSION_STORAGE_KEY = 'spence_tab_session_admin';

export type TabSessionTokens = {
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
};

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage?.getItem === 'function';
}

function storageKey(realm: SessionRealm): string {
  return realm === 'admin' ? ADMIN_TAB_SESSION_STORAGE_KEY : USER_TAB_SESSION_STORAGE_KEY;
}

function normalizeToken(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function hasAnyToken(tokens: TabSessionTokens): boolean {
  return Boolean(tokens.idToken || tokens.accessToken || tokens.refreshToken);
}

function parseTabSessionTokens(raw: string | null): TabSessionTokens | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object') {
      return null;
    }

    const record = parsed as Record<string, unknown>;
    const tokens: TabSessionTokens = {
      idToken: normalizeToken(record.idToken),
      accessToken: normalizeToken(record.accessToken),
      refreshToken: normalizeToken(record.refreshToken),
    };

    return hasAnyToken(tokens) ? tokens : null;
  } catch {
    return null;
  }
}

export function persistTabSessionTokens(realm: SessionRealm, tokens: TabSessionTokens): void {
  if (!canUseSessionStorage()) {
    return;
  }

  const normalized: TabSessionTokens = {
    idToken: normalizeToken(tokens.idToken),
    accessToken: normalizeToken(tokens.accessToken),
    refreshToken: normalizeToken(tokens.refreshToken),
  };

  try {
    if (!hasAnyToken(normalized)) {
      window.sessionStorage.removeItem(storageKey(realm));
      return;
    }

    window.sessionStorage.setItem(storageKey(realm), JSON.stringify(normalized));
  } catch {
    // Ignore quota / privacy mode.
  }
}

export function readTabSessionTokens(realm: SessionRealm): TabSessionTokens | null {
  if (!canUseSessionStorage()) {
    return null;
  }

  try {
    return parseTabSessionTokens(window.sessionStorage.getItem(storageKey(realm)));
  } catch {
    return null;
  }
}

export function clearTabSessionTokens(realm?: SessionRealm): void {
  if (!canUseSessionStorage()) {
    return;
  }

  try {
    if (realm) {
      window.sessionStorage.removeItem(storageKey(realm));
      return;
    }

    window.sessionStorage.removeItem(USER_TAB_SESSION_STORAGE_KEY);
    window.sessionStorage.removeItem(ADMIN_TAB_SESSION_STORAGE_KEY);
  } catch {
    // Ignore quota / privacy mode.
  }
}

/** Restore tab-scoped tokens into Redux. Must run before session validation. */
export function hydrateStoreFromTabSession(dispatch: Dispatch): void {
  const user = readTabSessionTokens('user');
  if (user) {
    dispatch(setUserSessionTokens(user));
  }

  const admin = readTabSessionTokens('admin');
  if (admin) {
    dispatch(setAdminSessionTokens(admin));
  }
}
